/**
 * Perguntas rápidas no lugar de window.prompt/confirm (que o Electron não tem e
 * que fogem do tema): `askText`, `askConfirm` e `askChoice` devolvem uma promessa e o
 * <PromptHost/> desenha um diálogo por vez.
 */
import { useState } from 'react';
import { create } from 'zustand';
import { useT } from '../i18n/index.js';
import { Button, Dialog, TextEdit } from './controls.js';

type TextAsk = { id: number; kind: 'text'; title: string; label?: string; initial: string; maxLength?: number; action?: string; allowEmpty?: boolean; secret?: boolean; resolve: (v: string | null) => void };
type ConfirmAsk = { id: number; kind: 'confirm'; title: string; message: string; action?: string; danger?: boolean; resolve: (v: boolean) => void };
/** Uma opção de `askChoice`: `hint` vai embaixo, menor; `current` marca a que vale hoje. */
export type ChoiceOption = { value: string; label: string; hint?: string; current?: boolean };
type ChoiceAsk = { id: number; kind: 'choice'; title: string; label?: string; options: ChoiceOption[]; empty?: string; resolve: (v: string | null) => void };
type Ask = TextAsk | ConfirmAsk | ChoiceAsk;

const useAsks = create<{ queue: Ask[] }>()(() => ({ queue: [] }));

let seq = 0;
const push = (ask: Omit<TextAsk, 'id'> | Omit<ConfirmAsk, 'id'> | Omit<ChoiceAsk, 'id'>) => useAsks.setState((s) => ({ queue: [...s.queue, { ...ask, id: ++seq } as Ask] }));
const pop = () => useAsks.setState((s) => ({ queue: s.queue.slice(1) }));

/** Pede um texto. `null` = cancelou. O texto volta sem espaços nas pontas. */
/** Pede um texto. `secret`: uma senha (escondida e sem tirar os espaços). */
export function askText(opts: { title: string; label?: string; initial?: string; maxLength?: number; action?: string; allowEmpty?: boolean; secret?: boolean }): Promise<string | null> {
  return new Promise((resolve) => push({ kind: 'text', initial: '', ...opts, resolve }));
}

export function askConfirm(opts: { title: string; message: string; action?: string; danger?: boolean }): Promise<boolean> {
  return new Promise((resolve) => push({ kind: 'confirm', ...opts, resolve }));
}

/** Escolhe uma opção da lista (o `value` dela). `null` = cancelou. `empty`: o texto da lista vazia. */
export function askChoice(opts: { title: string; label?: string; options: ChoiceOption[]; empty?: string }): Promise<string | null> {
  return new Promise((resolve) => push({ kind: 'choice', ...opts, resolve }));
}

export function PromptHost() {
  const top = useAsks((s) => s.queue[0]);
  if (!top) return null;
  if (top.kind === 'choice') return <ChoicePrompt key={top.id} ask={top} />;
  return top.kind === 'text' ? <TextPrompt key={top.id} ask={top} /> : <ConfirmPrompt key={top.id} ask={top} />;
}

/** a partir de quantas opções o campo de busca aparece */
const CHOICE_SEARCH_FROM = 8;

function ChoicePrompt({ ask }: { ask: ChoiceAsk }) {
  const t = useT();
  const [query, setQuery] = useState('');
  const done = (v: string | null) => {
    pop();
    ask.resolve(v);
  };
  const withSearch = ask.options.length >= CHOICE_SEARCH_FROM;
  const fold = (text: string) => text.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
  const shown = withSearch && query.trim() ? ask.options.filter((o) => fold(`${o.label} ${o.hint ?? ''}`).includes(fold(query.trim()))) : ask.options;
  return (
    <Dialog
      title={ask.title}
      onClose={() => done(null)}
      actions={
        <Button variant="secondary" onClick={() => done(null)}>
          {t('create.cancel')}
        </Button>
      }
    >
      {ask.label ? <p className="rd-prompt__label">{ask.label}</p> : null}
      {withSearch ? (
        <TextEdit look="filled" value={query} onChange={setQuery} placeholder={t('common.search')} aria-label={t('common.search')} autoFocusOnMount />
      ) : null}
      <ul className="rd-choice__list" aria-label={ask.label ?? ask.title}>
        {shown.map((o) => (
          <li key={o.value}>
            <button type="button" className="rd-choice__option" aria-current={o.current ? 'true' : undefined} onClick={() => done(o.value)}>
              <strong>{o.label}</strong>
              {o.hint ? <span>{o.hint}</span> : null}
            </button>
          </li>
        ))}
        {shown.length === 0 ? <li className="rd-choice__none">{ask.options.length ? t('common.noResults') : (ask.empty ?? t('common.noResults'))}</li> : null}
      </ul>
    </Dialog>
  );
}

function TextPrompt({ ask }: { ask: TextAsk }) {
  const t = useT();
  const [value, setValue] = useState(ask.initial);
  const done = (v: string | null) => {
    pop();
    ask.resolve(v);
  };
  const ok = ask.allowEmpty || value.trim() !== '';
  return (
    <Dialog
      title={ask.title}
      onClose={() => done(null)}
      actions={
        <>
          <Button variant="secondary" onClick={() => done(null)}>
            {t('create.cancel')}
          </Button>
          <Button variant="action" disabled={!ok} onClick={() => done(ask.secret ? value : value.trim())}>
            {ask.action ?? t('common.ok')}
          </Button>
        </>
      }
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (ok) done(ask.secret ? value : value.trim());
        }}
      >
        {ask.label ? <p className="rd-prompt__label">{ask.label}</p> : null}
        <TextEdit
          look="filled"
          value={value}
          onChange={setValue}
          autoFocusOnMount
          maxLength={ask.maxLength ?? 80}
          aria-label={ask.label ?? ask.title}
          type={ask.secret ? 'password' : 'text'}
          autoComplete={ask.secret ? 'off' : undefined}
          onFocus={(e) => e.currentTarget.select()}
        />
      </form>
    </Dialog>
  );
}

function ConfirmPrompt({ ask }: { ask: ConfirmAsk }) {
  const t = useT();
  const done = (v: boolean) => {
    pop();
    ask.resolve(v);
  };
  return (
    <Dialog
      title={ask.title}
      onClose={() => done(false)}
      actions={
        <>
          <Button variant="secondary" onClick={() => done(false)}>
            {t('create.cancel')}
          </Button>
          <Button variant="action" className={ask.danger ? 'rd-btn--danger' : undefined} autoFocus onClick={() => done(true)}>
            {ask.action ?? t('common.ok')}
          </Button>
        </>
      }
    >
      <p style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{ask.message}</p>
    </Dialog>
  );
}
